// Activo Fijo → Toma Física → Toma física / Conteo.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Camera, ClipboardCheck, CloudOff, Loader2, Lock, Plus, RefreshCw, ScanLine, Unlock } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { getErrorMessage } from '@/lib/api/client';
import { Campo, ESTADO_DET_CLS, Modal, Opcion, TomaDetalle, TomaRow, af, afRpc, fieldCls } from './shared';
import { fechaLocal } from '@/lib/utils/fecha';
import { EscanerQR } from '@/components/ui/EscanerQR';
import { drenar, encolar, pendientes } from './tomaOffline';

const hoy = () => fechaLocal();

export function TomaFisicaAF() {
  const qc = useQueryClient();
  const { data: tomas = [], isFetching } = useQuery<TomaRow[]>({ queryKey: ['af-tomas'], queryFn: () => afRpc<TomaRow[]>('af_tomas') });
  const { data: ubicaciones = [] } = useQuery<Opcion[]>({ queryKey: ['af-ubicaciones-op'], queryFn: () => af.list<Opcion>('ubicaciones', '?select=id,nombre&deleted_at=is.null&es_activo=is.true&order=nombre') });
  const [sel, setSel] = useState<TomaRow | null>(null);
  const [abierto, setAbierto] = useState(false);
  const [nueva, setNueva] = useState({ fecha: hoy(), ubicacion_id: '', responsable: '', observaciones: '' });
  const inval = () => { void qc.invalidateQueries({ queryKey: ['af-tomas'] }); void qc.invalidateQueries({ queryKey: ['af-toma-det'] }); };

  const crear = useMutation({
    mutationFn: () => afRpc<any>('af_toma_crear', { _fecha: nueva.fecha, _ubicacion_id: nueva.ubicacion_id || null, _responsable: nueva.responsable || null, _observaciones: nueva.observaciones || null }),
    onSuccess: (r) => { toast.success(`Toma creada · ${r.esperados} activo(s) esperados`); setAbierto(false); inval(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (sel) return <PanelToma toma={sel} ubicaciones={ubicaciones} onBack={() => { setSel(null); inval(); }} />;

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-xl font-semibold flex items-center gap-2"><ClipboardCheck className="w-5 h-5" /> Toma física / Conteo</h1>
        <Button onClick={() => { setNueva({ fecha: hoy(), ubicacion_id: '', responsable: '', observaciones: '' }); setAbierto(true); }}><Plus className="w-4 h-4 mr-1" /> Nueva toma</Button>
      </div>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Tomas ({tomas.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground"><th className="py-2 pr-3">Fecha</th><th className="py-2 pr-3">Ubicación</th><th className="py-2 pr-3">Responsable</th><th className="py-2 pr-3 text-right">Esperados</th><th className="py-2 pr-3 text-right">Encontrados</th><th className="py-2 pr-3 text-right">Faltantes</th><th className="py-2 pr-3 text-right">Sobrantes</th><th className="py-2 pr-3">Estado</th><th className="py-2"></th></tr></thead>
            <tbody>
              {tomas.length === 0 && !isFetching && <tr><td colSpan={9} className="py-8 text-center text-muted-foreground">Sin tomas</td></tr>}
              {tomas.map((t) => (
                <tr key={t.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 whitespace-nowrap">{t.fecha}</td>
                  <td className="py-2 pr-3">{t.ubicacion ?? 'Toda la empresa'}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{t.responsable ?? '—'}</td>
                  <td className="py-2 pr-3 text-right">{t.esperados}</td>
                  <td className="py-2 pr-3 text-right text-estable">{t.encontrados}</td>
                  <td className="py-2 pr-3 text-right text-red-600 dark:text-red-400">{t.faltantes}</td>
                  <td className="py-2 pr-3 text-right text-pendiente">{t.sobrantes}</td>
                  <td className="py-2 pr-3"><span className={`rounded-full px-2 py-0.5 text-xs ${t.estado === 'abierta' ? 'bg-pendiente-soft text-pendiente' : 'bg-estable-soft text-estable'}`}>{t.estado}</span></td>
                  <td className="py-2 text-right"><Button size="sm" variant="outline" onClick={() => setSel(t)}>Abrir</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {abierto && (
        <Modal title="Nueva toma física" onClose={() => setAbierto(false)}>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Fecha *"><input type="date" className={fieldCls} value={nueva.fecha} onChange={(e) => setNueva({ ...nueva, fecha: e.target.value })} /></Campo>
              <Campo label="Ubicación (alcance)"><select className={fieldCls} value={nueva.ubicacion_id} onChange={(e) => setNueva({ ...nueva, ubicacion_id: e.target.value })}><option value="">Toda la empresa</option>{ubicaciones.map((u) => <option key={u.id} value={u.id}>{u.nombre}</option>)}</select></Campo>
            </div>
            <Campo label="Responsable"><input className={fieldCls} value={nueva.responsable} onChange={(e) => setNueva({ ...nueva, responsable: e.target.value })} /></Campo>
            <Campo label="Observaciones"><textarea className={`${fieldCls} h-16`} value={nueva.observaciones} onChange={(e) => setNueva({ ...nueva, observaciones: e.target.value })} /></Campo>
            <p className="text-xs text-muted-foreground">Al crear se congela la lista de activos activos del alcance como "esperados".</p>
            <div className="flex justify-end gap-2 border-t pt-3">
              <Button variant="outline" onClick={() => setAbierto(false)}>Cancelar</Button>
              <Button isLoading={crear.isPending} onClick={() => crear.mutate()}>Crear toma</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function PanelToma({ toma, ubicaciones, onBack }: { toma: TomaRow; ubicaciones: Opcion[]; onBack: () => void }) {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const abierta = toma.estado === 'abierta';
  const { data: det = [], isFetching } = useQuery<TomaDetalle[]>({ queryKey: ['af-toma-det', toma.id], queryFn: () => afRpc<TomaDetalle[]>('af_toma_detalle', { _toma_id: toma.id }) });
  const [placa, setPlaca] = useState('');
  const [ubicHallada, setUbicHallada] = useState('');
  const [escaneando, setEscaneando] = useState(false);
  const [enCola, setEnCola] = useState(() => pendientes(toma.id).length);
  const inval = () => { void qc.invalidateQueries({ queryKey: ['af-toma-det', toma.id] }); void qc.invalidateQueries({ queryKey: ['af-tomas'] }); };

  // `ubicHallada` se lee de una ref porque el escáner sigue abierto mientras el
  // usuario cambia el selector: sin la ref, el callback se quedaría con el valor
  // que tenía al abrir la cámara.
  const ubicRef = useRef(ubicHallada); ubicRef.current = ubicHallada;

  const contar = useMutation({
    mutationFn: () => afRpc<any>('af_toma_contar', { _toma_id: toma.id, _activo_id: null, _placa: placa.trim(), _ubicacion_hallada_id: ubicHallada || null, _custodio_hallado_id: null, _observacion: null }),
    onSuccess: (r) => { toast.success(r.sobrante ? `Sobrante registrado (${placa})` : `Contado ${placa}`); setPlaca(''); inval(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  // Lo que llega del QR es la placa. Si no hay red, se encola en el propio
  // teléfono: perder un conteo obliga a volver a buscar el activo.
  const contarPlaca = useCallback(async (p: string) => {
    const val = p.trim();
    if (!val) return;
    try {
      const r = await afRpc<any>('af_toma_contar', {
        _toma_id: toma.id, _activo_id: null, _placa: val,
        _ubicacion_hallada_id: ubicRef.current || null, _custodio_hallado_id: null, _observacion: null,
      });
      toast.success(r.sobrante ? `Sobrante: ${val}` : `Contado ${val}`);
      inval();
    } catch (e) {
      const err = e as { code?: string; response?: { status?: number } };
      if (err?.code === 'ERR_NETWORK' || !err?.response) {
        setEnCola(encolar(toma.id, { placa: val, ubicacion_hallada_id: ubicRef.current || null, ts: Date.now() }));
        toast.warning(`Sin red · ${val} queda en cola`);
      } else {
        toast.error(getErrorMessage(e));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toma.id]);

  const sincronizar = useCallback(async () => {
    const r = await drenar(toma.id);
    setEnCola(r.quedan);
    if (r.enviados) { toast.success(`${r.enviados} conteo(s) sincronizado(s)`); inval(); }
    if (r.rechazados.length) toast.error(`Rechazados por el servidor: ${r.rechazados.join(', ')}`);
    if (!r.enviados && !r.rechazados.length && r.quedan) toast.warning('Sigue sin haber conexión');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toma.id]);

  // Al volver la red, la cola se va sola: nadie debería tener que acordarse.
  useEffect(() => {
    if (!enCola) return;
    const alVolver = () => { void sincronizar(); };
    window.addEventListener('online', alVolver);
    return () => window.removeEventListener('online', alVolver);
  }, [enCola, sincronizar]);
  const cerrar = useMutation({ mutationFn: () => afRpc('af_toma_cerrar', { _toma_id: toma.id }), onSuccess: () => { toast.success('Toma cerrada'); inval(); onBack(); }, onError: (e) => toast.error(getErrorMessage(e)) });
  const reabrir = useMutation({ mutationFn: () => afRpc('af_toma_reabrir', { _toma_id: toma.id }), onSuccess: () => { toast.success('Toma reabierta'); inval(); onBack(); }, onError: (e) => toast.error(getErrorMessage(e)) });

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex items-center justify-between gap-3">
        <button className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" onClick={onBack}><ArrowLeft className="w-4 h-4" /> Volver</button>
        <div className="flex gap-2">
          {abierta
            ? <Button size="sm" variant="outline" onClick={async () => { if (await dlg.confirmar('¿Cerrar la toma?', { detalle: 'Lo no contado quedará como faltante.' })) cerrar.mutate(); }}><Lock className="w-4 h-4 mr-1" /> Cerrar toma</Button>
            : <Button size="sm" variant="outline" onClick={() => reabrir.mutate()}><Unlock className="w-4 h-4 mr-1" /> Reabrir</Button>}
        </div>
      </div>
      <h1 className="text-lg font-semibold">Toma {toma.fecha} · {toma.ubicacion ?? 'Toda la empresa'} <span className={`ml-2 rounded-full px-2 py-0.5 text-xs ${abierta ? 'bg-pendiente-soft text-pendiente' : 'bg-estable-soft text-estable'}`}>{toma.estado}</span></h1>

      {abierta && (
        <Card><CardContent className="p-4 space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            <Campo label="Placa (escanea o escribe)"><input className={`${fieldCls} w-48`} value={placa} autoFocus onChange={(e) => setPlaca(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && placa.trim()) contar.mutate(); }} placeholder="AF-00001" /></Campo>
            <Campo label="Ubicación hallada (opcional)"><select className={`${fieldCls} w-44`} value={ubicHallada} onChange={(e) => setUbicHallada(e.target.value)}><option value="">= esperada</option>{ubicaciones.map((u) => <option key={u.id} value={u.id}>{u.nombre}</option>)}</select></Campo>
            <Button isLoading={contar.isPending} disabled={!placa.trim()} onClick={() => contar.mutate()}><ScanLine className="w-4 h-4 mr-1" /> Contar</Button>
            <Button variant="outline" onClick={() => setEscaneando(true)}><Camera className="w-4 h-4 mr-1" /> Escanear con la cámara</Button>
          </div>
          <p className="text-xs text-muted-foreground">
            El escáner queda abierto: apunta a una placa tras otra y cada una se cuenta sola. La ubicación hallada que elijas arriba se aplica a todo lo que escanees.
          </p>

          {enCola > 0 && (
            <div className="flex flex-wrap items-center gap-3 rounded-md border border-amber-400 bg-amber-50 p-3 text-sm dark:border-amber-900/50 dark:bg-amber-900/15">
              <CloudOff className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
              <span className="text-amber-800 dark:text-amber-300">
                <b>{enCola}</b> conteo(s) guardados en este dispositivo, sin enviar. Se envían solos al volver la red.
              </span>
              <Button size="sm" variant="outline" onClick={() => void sincronizar()}><RefreshCw className="mr-1 h-4 w-4" /> Sincronizar ahora</Button>
            </div>
          )}
        </CardContent></Card>
      )}

      {escaneando && (
        <EscanerQR integrado titulo={`Toma ${toma.fecha}`}
          onLeer={(txt) => void contarPlaca(txt)}
          onCerrar={() => setEscaneando(false)} />
      )}

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Detalle ({det.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground"><th className="py-2 pr-3">Placa</th><th className="py-2 pr-3">Activo</th><th className="py-2 pr-3">Ubic. esperada</th><th className="py-2 pr-3">Ubic. hallada</th><th className="py-2 pr-3">Estado</th></tr></thead>
            <tbody>
              {det.map((d) => (
                <tr key={d.id} className="border-b last:border-0">
                  <td className="py-2 pr-3 font-mono">{d.placa}</td>
                  <td className="py-2 pr-3">{d.activo}{d.es_sobrante && <span className="ml-2 text-xs text-pendiente">(sobrante)</span>}{d.ubic_incorrecta && <span className="ml-2 text-xs text-amber-600 dark:text-amber-400">(ubicación distinta)</span>}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{d.ubic_esperada ?? '—'}</td>
                  <td className="py-2 pr-3">{d.ubic_hallada ?? '—'}</td>
                  <td className="py-2 pr-3"><span className={`rounded-full px-2 py-0.5 text-xs ${ESTADO_DET_CLS[d.estado] ?? 'bg-muted'}`}>{d.estado}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
